import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { keys } from '@/lib/queryKeys';
import {
  listPrescriptionsForVisit, createPrescription, savePrescription, setPrescriptionStatus,
} from './clinicalApi';
import type { PrescriptionInput, PrescriptionStatus } from './types';

/**
 * Checked on mount so refreshing mid-consultation resumes the visit's
 * existing draft/active prescription instead of the widget creating a
 * second one. Branch-scoped, same branchVersion convention as
 * useVisitDiagnoses.ts.
 */
export function useVisitPrescriptions(visitId: number | null) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.prescriptions.list({ visit_id: visitId ?? undefined }), branchVersion],
    queryFn: () => listPrescriptionsForVisit(visitId as number),
    enabled: visitId != null,
  });
}

function invalidatePrescriptions(queryClient: ReturnType<typeof useQueryClient>, id?: number, uhid?: string) {
  queryClient.invalidateQueries({ queryKey: keys.prescriptions.list() });
  if (id != null) queryClient.invalidateQueries({ queryKey: keys.prescriptions.detail(id) });
  if (uhid) queryClient.invalidateQueries({ queryKey: keys.clinical.prescriptionsForPatient(uhid) });
}

export function useCreatePrescription(uhid?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PrescriptionInput) => createPrescription(input),
    onSuccess: (data) => invalidatePrescriptions(queryClient, data.id, uhid),
  });
}

export function useSavePrescription(uhid?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch, ifMatch }: {
      id: number;
      patch: { notes?: string | null; status?: PrescriptionStatus; items?: PrescriptionInput['items'] };
      ifMatch?: string;
    }) => savePrescription(id, patch, ifMatch),
    onSuccess: (data) => invalidatePrescriptions(queryClient, data.id, uhid),
  });
}

export function useSetPrescriptionStatus(uhid?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, reason, ifMatch }: { id: number; status: PrescriptionStatus; reason?: string; ifMatch?: string }) =>
      setPrescriptionStatus(id, status, reason, ifMatch),
    onSuccess: (data) => invalidatePrescriptions(queryClient, data.id, uhid),
  });
}
